import * as THREE from 'three';
import { getStateCallbacks, Room, SeatReservation } from 'colyseus.js';
import type { AuthUser } from '../core/state/auth-store';
import { joinArenaViaReservation } from './net/arena-client';
import { sendMove, sendAim, sendSprint, sendCrouch, sendJump, sendAttack, sendParry, sendBlock } from './net/colyseus-client';
import type { ClientClockSync } from './net/clock-sync';
import { localPlayerFadeAlpha } from './render/proximity-fade';
import { DungeonInstanceScene } from './render/dungeon-instance-scene';
import { CharacterVisual } from './render/character-visual';
import { buildInstancedLayout } from './render/instanced-layout';
import { InputController } from './input/input-controller';
import { TouchControls, isTouchDevice } from './input/touch-controls';
import { OverShoulderCameraController, normalizeAngle } from './input/over-shoulder-camera-controller';
import { bindAudioToSettings, bindCameraToSettings, bindGameplayToSettings } from './ui/settings/apply-settings';
import { RemoteInterpolator } from './sim/interpolator';
import { ServerRenderClock } from './sim/render-clock';
import { DebugHud } from './ui/debug-hud';
import { CombatHud, type HudPartyMember } from './ui/combat-hud';
import { ActionBar, AbilityStateView } from './ui/action-bar';
import { AbilityNotice } from './ui/ability-notice';
import { pickAbilityTarget, pickCycleTarget, pickImplicitTarget, partyKeyTarget, type TargetCandidate } from './sim/targeting';
import { TargetRing } from './render/target-ring';
import { DamageNumbers, combatTextFor } from './ui/damage-numbers';
import { ArenaRoundBanner, type ArenaPhase } from './ui/arena-round-banner';
import { AbilityFxLayer, loadAbilityFxSprites } from './render/ability-fx';
import { ABILITY_FX_DURATION_MS, abilityVisual, type AbilityFxKind } from './config/ability-vfx';
import type { AnimIntent } from './render/animation-map';
import { rosterEntry } from './config/character-roster';
import { MONSTER_DISPLAY_NAME } from './config/monster-roster';
import { sendAbilitySlot, sendTarget } from './net/dungeon-instance-client';
import { DamageFlash } from './ui/damage-flash';
import { ArenaRoundOverlay, type ArenaPersonalOutcome } from './ui/arena-round-overlay';
import { RunMenu } from './ui/run-menu';
import { MouseLookPrompt } from './ui/mouse-look-prompt';
import { EntryVeil } from './ui/entry-veil';
import { LevelUpBanner } from './ui/level-up-banner';
import type { EntityStateName } from './config/tuning';
import type { CharacterClassId, WeaponClass } from './config/character-roster';
import { monsterRosterEntry, MonsterArchetype } from './config/monster-roster';
import { AudioEngine } from './audio/audio-engine';
import { RemoteFootsteps, FootstepEntity } from './audio/remote-footsteps';
import { FrameTimer } from './sim/frame-timer';
import { LocalJump, JUMP_DURATION_MS, remoteJumpOffset, isFreshJump, isAirborne } from './sim/jump';
import { t, N_ } from './i18n/i18n';

/**
 * design/gdd/pvp-arena.md — Gameplay Redesign Phase 5's frontend. Deliberately
 * a much thinner sibling of dungeon-instance-bootstrap.ts: no world-objects
 * (no doors/chests/portcullis/braziers — Arena has none), no loot panel, no
 * insurance, no lantern, no interact, no fly-through, no descend. Reuses
 * `DungeonInstanceScene` directly rather than a parallel scene class — every
 * method it needs (setLayoutGroup/addPlayer/addMonster/followLocalPlayer/
 * shakeCamera/render/dispose) has no hard dependency on world-objects
 * existing; this file simply never calls the door/chest/portcullis/brazier
 * beacon methods.
 */

/** camera-and-controls.md amendment (2026-08-25, fifth) — see dungeon-instance-bootstrap.ts's identical mirror of the server's `canMove`. */
const TURNABLE_STATES = new Set<EntityStateName>(['IDLE', 'MOVING', 'SPRINTING', 'CROUCHING', 'BLOCKING']);

const LEGEND = N_('WASD move · Shift sprint · Ctrl crouch · Space jump · LMB attack (J/K/L for L/OH/R) · F parry · RMB block (hold) · 1-9 abilities · Tab target · Mouse look (click to capture, Esc to release) · MMB shoulder swap');

/**
 * Arena rework (2026-08-26) — friend or foe, in the one place a player is
 * actually looking: at the bodies.
 *
 * The arena never set `squadId`, so no client-side friend-or-foe distinction
 * was even possible — every fighter was an anonymous stranger. Rim light is
 * the cheapest legible answer available (`CharacterVisual.applyRimLight`
 * already exists for monsters) and works at any distance, in any lighting,
 * from behind.
 */
const ALLY_RIM_COLOR = 0x4a9eff;
const ENEMY_RIM_COLOR = 0xff4d4d;
const RIM_POWER = 2.2;
const RIM_INTENSITY = 0.85;
/** Every ability's caster animation runs for the global cooldown — see dungeon-instance-bootstrap.ts. */
const ABILITY_ANIMATION_SECONDS = 1.25;
const CASTER_FX: ReadonlySet<AbilityFxKind> = new Set<AbilityFxKind>(['WARD', 'SHROUD', 'SHOUT', 'SWEEP', 'BOLT', 'LINK']);
const HIT_FX: Partial<Record<AbilityFxKind, AbilityFxKind>> = {
  STRIKE: 'STRIKE', SHOUT: 'STRIKE', SWEEP: 'STRIKE', LINK: 'STRIKE', BLESSING: 'BLESSING', SNARE: 'SNARE',
};
const HELD_FX: ReadonlySet<AbilityFxKind> = new Set<AbilityFxKind>(['WARD', 'SNARE']);
const ABILITY_FX_INTENT: Record<AbilityFxKind, AnimIntent> = {
  WARD: 'ABILITY_WARD', SHROUD: 'ABILITY_STEALTH', SHOUT: 'ABILITY_SHOUT', SWEEP: 'ABILITY_SWEEP',
  STRIKE: 'ABILITY_STRIKE', BOLT: 'ABILITY_CAST', BLESSING: 'ABILITY_CAST', SNARE: 'ABILITY_CAST',
  LINK: 'ABILITY_SHOUT',
};

interface AbilityUsedMessage {
  playerId: string;
  abilityId: string;
  targetId?: string;
  serverTs: number;
  restoredHp?: number;
  radiusM?: number;
  durationMs?: number;
  hits?: { id: string; damage?: number; died?: boolean }[];
}
const MONSTER_HEALTH_BAR_HEIGHT_M = 2.3;
/** Just over a fighter's head — lower than a monster's because players are shorter than a Bone Warden. */
const PLAYER_HEALTH_BAR_HEIGHT_M = 2.0;
/**
 * Added to a monster's distance when Tab cycles, so a neutral skeleton never
 * wins the target over a rival fighter standing further away. The mobs are a
 * hazard; the people are the round.
 */
const TARGET_MONSTER_BIAS_M = 6;
const HIT_STOP_MS = 70;
const HIT_STOP_TIME_SCALE = 0.05;
const SHAKE_HIT_TAKEN_M = 0.12;
const SHAKE_HIT_LANDED_M = 0.05;
const SHAKE_DURATION_MS = 180;
/** A monster's display name, translated — the raw archetype only for one this build does not know. */
function monsterName(archetype: string): string {
  const english = MONSTER_DISPLAY_NAME[archetype as MonsterArchetype];
  return english ? t(english) : archetype;
}

const OBJECTIVE = N_('Objective: last team standing wins. Watch for the neutral Rattlers/Bonewrights loose in the arena.');
const MOVE_SEND_INTERVAL_MS = 50; // 20Hz, matches dungeon-instance-bootstrap.ts's own send cadence
const DIRECTION_SEND_EPSILON = 0.001;
/** camera-and-controls.md amendment (2026-08-25, fifth) — aim rides the same 20Hz budget as movement. */
const AIM_SEND_INTERVAL_MS = 50;
const AIM_SEND_EPSILON = 0.001;

let currentTeardown: (() => void) | null = null;

/**
 * `onMatchEnd` (sconce-hub.md Core Rule #8 pattern) — fired when the arena
 * room disconnects (round over, or any other disconnect), so the hub screen
 * can reconnect the Socket.io gateway and remount itself.
 */
export function mountArena(
  root: HTMLElement,
  _user: AuthUser,
  _onLogout: () => void,
  reservation: SeatReservation,
  onMatchEnd?: () => void,
): () => void {
  root.innerHTML = '';

  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;display:block;';
  root.appendChild(canvas);

  /**
   * Mounted before anything else, and before a single frame is drawn — see
   * ui/entry-veil.ts for the capture that prompted it. Everything below builds
   * its own chrome immediately, and the chrome is exactly what made an empty
   * canvas read as a broken game rather than a loading one.
   */
  const entryVeil = new EntryVeil(root, t('Entering the Arena'), t('Taking your place on the sand…'));

  const dScene = new DungeonInstanceScene(canvas);
  const input = new InputController();
  /** The arena is played on a phone too — same controls as the dungeon, same file. */
  const touch = isTouchDevice() ? new TouchControls(root) : null;
  let touchSprintSent = false;
  const camera = new OverShoulderCameraController(canvas);
  const hud = new DebugHud(root, t(LEGEND), t(OBJECTIVE));
  const damageFlash = new DamageFlash(root);
  /**
   * The same in-run menu the dungeon uses — the user's "no way to manually
   * quit" is just as true here.
   *
   * Abandoning an arena round is simply leaving: there is no Run row and no
   * loot at stake (`pvp-arena.md` Core Rule #5), so the honest action is to
   * drop the seat and go home. `room.leave()` is what already fires
   * `onMatchEnd`, so this reuses the exact path a finished round takes.
   */
  const runMenu = new RunMenu(root, {
    onAbandon: () => { room?.leave(); },
    confirmText: t('Leaving forfeits this round. There is no loot at stake here — you simply return to the Sconce.'),
    onVisibilityChange: (open) => { if (open) { input.releaseAll(); touch?.releaseAll(); } },
  }, t(LEGEND));
  // Arena rework (2026-08-26) — the arena's client had *only* the debug
  // overlay: no health, no stamina, no resource, no target, no abilities, no
  // damage numbers. Everything below already existed and was already wired up
  // in the dungeon; the arena simply never mounted any of it.
  const combatHud = new CombatHud(root);
  const actionBar = new ActionBar(root);
  /**
   * Why a press did nothing, in words (2026-10-16). The arena never mounted
   * one: every refusal went only to the developer log, so a too-far or
   * no-target press in the pit was silence.
   */
  const abilityNotice = new AbilityNotice(root);
  abilityNotice.touch = touch !== null;
  /** The floor mark under the current target — see `render/target-ring.ts`. */
  const targetRing = new TargetRing(dScene.scene);
  const damageNumbers = new DamageNumbers(root);

  /* Same preferences as a delve — the Arena is a different mode, not a
     different set of controls. There is no fly-through here to skip. */
  const unbindSettings: Array<() => void> = [
    bindCameraToSettings(camera),
    bindGameplayToSettings((g) => {
      damageNumbers.setEnabled(g.damageNumbers);
      damageFlash.setEnabled(g.hitFlash);
      hud.setDetailVisible(g.showPerformance);
    }),
  ];
  const roundBanner = new ArenaRoundBanner(root);
  // Painted effect sprites (2026-08-26). Loaded here and owned by the layer;
  // if any of them fail to arrive the layer falls back to its procedural
  // shapes, so an effect is never silently invisible.
  const abilityFx = new AbilityFxLayer(dScene.scene, loadAbilityFxSprites());
  const roundOverlay = new ArenaRoundOverlay(root);
  if (onMatchEnd) {
    roundOverlay.onReturnToHub = () => { currentTeardown?.(); onMatchEnd(); };
  }
  const levelUpBanner = new LevelUpBanner(root);
  const mouseLookPrompt = new MouseLookPrompt(root);
  camera.onLockChange = (locked) => mouseLookPrompt.setLocked(locked);
  mouseLookPrompt.setLocked(false);
  const audioEngine = new AudioEngine();
  unbindSettings.push(bindAudioToSettings(audioEngine));
  const remoteFootsteps = new RemoteFootsteps();
  /** Jump (2026-10-16) — the local player's predicted takeoff; see `sim/jump.ts`. */
  const localJump = new LocalJump();
  /** The arena has no strata; its floors are the Cellars' stone. */
  const ARENA_FOOTSTEP_STRATUM = 1;
  audioEngine.ensureStarted(); // see dungeon-instance-bootstrap.ts's identical call for why this can't wait for a specific gesture
  input.start();
  camera.start();

  const visuals = new Map<string, CharacterVisual>();
  const remoteInterpolators = new Map<string, RemoteInterpolator>();
  /** Arena rework (2026-08-26) — the ability bar's private state, and the soft target it acts on. */
  let abilityState: AbilityStateView | null = null;
  let localTargetId = '';
  let localTargetIsExplicit = false;
  /** Teammates in the order the party frames draw them — what F2–F4 index into. */
  let partyFrameOrder: string[] = [];
  /** Which bodies have already been tinted for their team, so the rim light is applied once. */
  const rimApplied = new Set<string>();
  /** Reused every frame by the damage-number projector — no per-frame allocation. */
  const projectVec = new THREE.Vector3();
  let roundStartedAtLocalMs: number | null = null;
  /** Tallied from `combat.result` for the round summary — see that handler. */
  let localKills = 0;
  let localDamageDealt = 0;
  const monsterVisuals = new Map<string, CharacterVisual>();
  const monsterInterpolators = new Map<string, RemoteInterpolator>();

  let disposed = false;
  /** Set once the arena's geometry is in the scene — half of what the veil waits on. */
  let layoutReady = false;
  /**
   * Frames drawn with both the geometry and the local fighter's body present.
   *
   * Two rather than one, deliberately: the frame that *adds* the body is drawn
   * before its first `setPose`, so lifting on it would reveal the fighter at
   * the world origin for one frame — outside the pit, which is the exact
   * artefact `players.onAdd`'s snapshot seeding exists to prevent.
   */
  let framesWithWorld = 0;
  /**
   * When the veil comes down whether or not the world arrived.
   *
   * A veil that waits forever on a body that never loads is a worse failure
   * than the black frame it replaced: at least a black frame can be quit out
   * of. Fifteen seconds is far past any measured load (~1.7s warm, and the
   * room's own join grace is 20s), so reaching it means something is wrong —
   * and a player who can see that is better off than one who cannot.
   */
  const veilDeadlineMs = performance.now() + 15_000;
  let room: Room | null = null;
  let clockSync: ClientClockSync | null = null;
  let localSessionId: string | null = null;
  let localUserId: string | null = null;
  /**
   * The shutter the whole world is photographed through — every body, and the
   * camera that follows one of them, is drawn from a sample taken at this
   * time. See sim/render-clock.ts for the jitter that came of resetting it
   * outright on every patch.
   */
  const renderClock = new ServerRenderClock(performance.now());
  let lastSentDirection = { x: 0, z: 0 };
  let lastMoveSentAt = 0;
  /** camera-and-controls.md amendment (2026-08-25, fifth) — see dungeon-instance-bootstrap.ts for the cadence rationale. */
  let lastSentAimYaw: number | null = null;
  let lastAimSentAt = 0;
  let cameraAligned = false;
  let lastLocalHp: number | null = null;
  let hitStopUntilMs = 0;

  /** Space / the touch Jump plate (2026-10-16) — see dungeon-instance-bootstrap.ts's `jumpLocal`. */
  function jumpLocal(): void {
    if (!room || !localSessionId) return;
    const me = room.state.players.get(localSessionId);
    if (!me || !localJump.tryStart(estimatedServerTimeNow(), me.state, me.stamina)) return;
    visuals.get(localSessionId)?.playJump(JUMP_DURATION_MS);
    sendJump(room);
  }

  /** Game-feel polish plan Phase 1 — see dungeon-instance-bootstrap.ts's identical helper for the full rationale (~130ms of input-to-animation latency this removes for the local player specifically). */
  function predictLocalState(nextState: EntityStateName, attackDirection?: string): void {
    if (!room || !localSessionId) return;
    const localPlayer = room.state.players.get(localSessionId);
    if (!localPlayer || localPlayer.state === 'DEAD' || localPlayer.state === 'DISCONNECTED') return;
    visuals.get(localSessionId)?.setState(nextState, localPlayer.weaponClass as WeaponClass, attackDirection);
  }

  /** Sends the current aim immediately — see dungeon-instance-bootstrap.ts's identical helper. */
  function flushAim(): void {
    if (!room) return;
    lastSentAimYaw = camera.viewYaw;
    lastAimSentAt = performance.now();
    sendAim(room, camera.viewYaw);
  }

  /** Which way to draw the local body this frame — see dungeon-instance-bootstrap.ts's identical helper. */
  function localRenderFacing(serverFacing: number): number {
    const state = room && localSessionId ? room.state.players.get(localSessionId)?.state : undefined;
    return state && TURNABLE_STATES.has(state as EntityStateName) ? camera.viewYaw : serverFacing;
  }

  const onCanvasMouseDown = (e: MouseEvent): void => {
    // The click that captures the pointer is not also a swing — see
    // dungeon-instance-bootstrap.ts. It matters more here: this is PvP.
    //
    // And so does the escape hatch beside it (2026-09-05): a duellist whose
    // browser refuses Pointer Lock would otherwise have no attack button at
    // all while their opponent has one.
    if (!camera.pointerLocked && !camera.lookUnavailable) return;
    if (e.button === 0) {
      if (room) { flushAim(); predictLocalState('ATTACK_WINDUP', 'OVERHEAD'); sendAttack(room, 'OVERHEAD'); }
    } else if (e.button === 2) {
      if (room) { flushAim(); predictLocalState('BLOCKING'); sendBlock(room, true); }
    }
  };
  const onCanvasMouseUp = (e: MouseEvent): void => {
    if (e.button === 2 && room) sendBlock(room, false);
  };
  canvas.addEventListener('mousedown', onCanvasMouseDown);
  canvas.addEventListener('mouseup', onCanvasMouseUp);

  function estimatedServerTimeNow(): number {
    return renderClock.now(performance.now());
  }

  (async () => {
    const joined = await joinArenaViaReservation(reservation);
    if (disposed) {
      joined.room.leave();
      return;
    }
    room = joined.room;
    clockSync = joined.clockSync;
    localSessionId = room.sessionId;
    const roomState = room.state;

    hud.logCombatEvent(`arena joined — ${joined.layout.teams.length} team(s)`);
    entryVeil.setStep(t('Raising the pit…'));
    const group = await buildInstancedLayout(joined.layout.instances);
    if (disposed) return;
    dScene.setLayoutGroup(group);
    layoutReady = true;
    entryVeil.setStep(t('Bringing the fighters in…'));

    const $ = getStateCallbacks(room);

    $(room.state).listen('serverTime', (value: number) => {
      renderClock.onServerTime(value, performance.now());
    });

    $(room.state).listen('roundState', (value: string) => {
      hud.logCombatEvent(`round: ${value}`);
      if (value === 'IN_PROGRESS') roundStartedAtLocalMs = performance.now();
    });

    /**
     * Arena rework (2026-08-26) — the bar, requested rather than pushed.
     *
     * Same reason the dungeon room documents: a private message sent from
     * `onJoin`, ahead of the client's first state patch, corrupts the schema
     * stream outright.
     */
    room.send('ability.request_state', {});
    room.onMessage('ability.state', (msg: AbilityStateView) => {
      abilityState = msg;
      actionBar.update(msg, estimatedServerTimeNow());
    });

    $(room.state).players.onAdd((player, sessionId: string) => {
      if (sessionId === localSessionId) localUserId = player.userId;
      const interpolator = new RemoteInterpolator();
      /**
       * Arena rework (2026-08-26) — seed the interpolator with where this
       * body *is*, before it has moved.
       *
       * Snapshots only arrived from `onChange`, so an entity that had not
       * moved yet had none — `sample()` returned null, `setPose` was never
       * called, and the body sat at the origin. With the new countdown, that
       * is every fighter for five seconds: the arena opened with all six
       * stacked in a corner and the camera outside the wall looking in,
       * because the camera follows the local player's sampled pose. Verified
       * in a live capture before the fix.
       */
      interpolator.pushSnapshot({
        serverTime: roomState.serverTime, x: player.x, z: player.z, facing: player.facing,
      });
      remoteInterpolators.set(sessionId, interpolator);

      // Jump (2026-10-16) — someone else's hop; the arc itself is drawn per frame.
      $(player).listen('jumpStartedAt', (value: number) => {
        if (sessionId === localSessionId || !isFreshJump(estimatedServerTimeNow(), value)) return;
        visuals.get(sessionId)?.playJump(JUMP_DURATION_MS);
      });

      $(player).onChange(() => {
        remoteInterpolators.get(sessionId)?.pushSnapshot({
          serverTime: roomState.serverTime, x: player.x, z: player.z, facing: player.facing,
        });
        visuals.get(sessionId)?.setState(player.state as EntityStateName, player.weaponClass as WeaponClass);
        if (sessionId !== localSessionId) {
          visuals.get(sessionId)?.setHealthFraction(player.hp / (player.hpMax || 1));
        }
        // `squadId` arrives with the first patch, which may be after the model
        // finished loading — so the tint is (re-)applied here too, and
        // `rimApplied` keeps it to once per body.
        const visual = visuals.get(sessionId);
        if (visual) applyTeamColour(sessionId, visual);

        if (sessionId === localSessionId) {
          if (lastLocalHp !== null && player.hp < lastLocalHp) damageFlash.trigger();
          lastLocalHp = player.hp;
        }
      });

      dScene.addPlayer(player.characterClass as CharacterClassId, player.skinAssetKey || undefined,
        { weaponModel: player.equippedWeapon || undefined, offhandModel: player.equippedOffhand || undefined },
      ).then((visual) => {
        if (disposed || !room?.state.players.has(sessionId)) { visual.dispose(); return; }
        visuals.set(sessionId, visual);
        visual.setState(player.state as EntityStateName, player.weaponClass as WeaponClass);
        applyTeamColour(sessionId, visual);
        // A health bar over every other fighter, the same one monsters get.
        // In a pit where anybody can be finishing anybody, "how close is that
        // one to dead" is the read the whole mode turns on.
        if (sessionId !== localSessionId) {
          visual.attachHealthBar(PLAYER_HEALTH_BAR_HEIGHT_M);
          visual.setHealthFraction(player.hp / (player.hpMax || 1));
        }
      }).catch((err) => {
        console.error('[arena] failed to load character model', err);
        hud.logCombatEvent(`character load failed: ${(err as Error).message ?? err}`);
      });
    });

    $(room.state).players.onRemove((_player, sessionId: string) => {
      visuals.get(sessionId)?.dispose();
      visuals.delete(sessionId);
      remoteInterpolators.delete(sessionId);
      rimApplied.delete(sessionId);
    });

    $(room.state).monsters.onAdd((monster, monsterId: string) => {
      const monsterInterpolator = new RemoteInterpolator();
      // Same reason as the players above: a mob standing still through the
      // countdown would otherwise render at the origin.
      monsterInterpolator.pushSnapshot({
        serverTime: roomState.serverTime, x: monster.x, z: monster.z, facing: monster.facing,
      });
      monsterInterpolators.set(monsterId, monsterInterpolator);
      const weaponClass = monsterRosterEntry(monster.archetype).weaponClass;

      $(monster).onChange(() => {
        monsterInterpolators.get(monsterId)?.pushSnapshot({
          serverTime: roomState.serverTime, x: monster.x, z: monster.z, facing: monster.facing,
        });
        monsterVisuals.get(monsterId)?.setState(monster.state as EntityStateName, weaponClass);
        monsterVisuals.get(monsterId)?.setHealthFraction(monster.hp / (monster.hpMax || 1)); // synced hpMax (2026-08-24) — onAdd fires on entering view, not on spawn
      });

      dScene.addMonster(monster.archetype as MonsterArchetype).then((visual) => {
        if (disposed || !room?.state.monsters.has(monsterId)) { visual.dispose(); return; }
        monsterVisuals.set(monsterId, visual);
        visual.setState(monster.state as EntityStateName, weaponClass);
        visual.attachHealthBar(MONSTER_HEALTH_BAR_HEIGHT_M);
        visual.setHealthFraction(monster.hp / (monster.hpMax || 1)); // synced hpMax (2026-08-24) — onAdd fires on entering view, not on spawn
      }).catch((err) => {
        console.error('[arena] failed to load monster model', err);
        hud.logCombatEvent(`monster model load failed: ${(err as Error).message ?? err}`);
      });
    });

    $(room.state).monsters.onRemove((_monster, monsterId: string) => {
      monsterVisuals.get(monsterId)?.dispose();
      monsterVisuals.delete(monsterId);
      monsterInterpolators.delete(monsterId);
    });

    room.onMessage('combat.result', (msg: { attackerId: string; defenderId: string; outcome: string; damage?: number; isRiposte?: boolean; staggers?: boolean; died?: boolean }) => {
      // What the round end reports about *you* (2026-09-06). Every figure it
      // needs was already crossing this handler and being thrown away: the
      // old results screen could say how long the round took and who was left
      // standing, but not one thing the player themselves had done.
      if (msg.attackerId === localSessionId && msg.defenderId !== localSessionId) {
        localDamageDealt += msg.damage ?? 0;
        if (msg.died) localKills += 1;
      }
      const short = (id: string) => id.slice(0, 4);
      const dmg = msg.damage !== undefined ? ` (${msg.damage} dmg${msg.isRiposte ? ', riposte' : ''})` : '';
      hud.logCombatEvent(`${short(msg.attackerId)} -> ${short(msg.defenderId)}: ${msg.outcome}${dmg}`);

      // Arena rework (2026-08-26) — the number, on the body. The arena had the
      // same payload the dungeon floats numbers from and did nothing with it.
      const struck = room?.state.players.get(msg.defenderId) ?? room?.state.monsters.get(msg.defenderId);
      const floater = combatTextFor(msg, localSessionId ?? '', (id) => isTeammate(id));
      if (floater && struck) damageNumbers.spawn(floater.text, floater.kind, struck.x, struck.z);

      // Implicit targeting: whatever you are trading blows with becomes your
      // target frame's subject, unless Tab has chosen one. Most players will
      // never press Tab and still need to know what they are hitting.
      // QA defect C (2026-10-16) — the dungeon's rule: only a hostile YOU hit,
      // only while you have no live target. Told to the server as well.
      const currentBody = localTargetId
        ? room?.state.players.get(localTargetId) ?? room?.state.monsters.get(localTargetId)
        : undefined;
      const implicit = pickImplicitTarget({
        attackerId: msg.attackerId,
        defenderId: msg.defenderId,
        selfId: localSessionId ?? '',
        currentId: localTargetId,
        currentLive: currentBody ? currentBody.state !== 'DEAD' : false,
        isHostile: (id) => !isTeammate(id)
          && (room?.state.players.has(id) || room?.state.monsters.has(id) || false),
      });
      if (implicit) setLocalTarget(implicit, false);

      /*
       * Every outcome, not only 'hit' — see the identical block in
       * dungeon-instance-bootstrap.ts. It matters more here than there: the
       * arena is player-versus-player melee, so parry-into-stagger is the whole
       * fight rather than one option within it.
       */
      if (struck) {
        audioEngine.playCombatOutcome({ x: struck.x, z: struck.z }, msg.outcome, {
          damage: msg.damage,
          isRiposte: msg.isRiposte,
          staggers: msg.staggers,
          targetIsBone: !room?.state.players.has(msg.defenderId),
        });
      }

      // A passive dodge (2026-10-16) — a sidestep on the defender, nothing more.
      if (msg.outcome === 'dodged') {
        (visuals.get(msg.defenderId) ?? monsterVisuals.get(msg.defenderId))?.playDodgeSidestep();
      }

      if (msg.outcome === 'hit') {
        const defenderVisual = visuals.get(msg.defenderId) ?? monsterVisuals.get(msg.defenderId);
        defenderVisual?.playImpactVfx();
        // Game Feel Polish Phase 2c — only for hits that don't already get a
        // full replace-animation from the state machine (STAGGERED/DEAD).
        if (!msg.staggers && !msg.died) void defenderVisual?.playHitFlinch();
        // The impact is played by `playCombatOutcome` above — see the same
        // note in dungeon-instance-bootstrap.ts.
        const tookHit = msg.defenderId === localSessionId;
        const landedHit = msg.attackerId === localSessionId;
        if (tookHit || landedHit) {
          hitStopUntilMs = performance.now() + HIT_STOP_MS;
          dScene.shakeCamera(tookHit ? SHAKE_HIT_TAKEN_M : SHAKE_HIT_LANDED_M, SHAKE_DURATION_MS);
        }
      }
    });

    /**
     * Arena rework (2026-08-26) — abilities, drawn exactly as the dungeon
     * draws them (`config/ability-vfx.ts`), because a fighter should not have
     * to learn their own kit twice.
     */
    room.onMessage('ability.used', (msg: AbilityUsedMessage) => {
      const visual = abilityVisual(msg.abilityId);
      const caster = room?.state.players.get(msg.playerId);
      const anchorFor = (id: string) => () => {
        const entity = room?.state.players.get(id) ?? room?.state.monsters.get(id);
        return entity ? { x: entity.x, z: entity.z } : null;
      };
      const durationFor = (kind: AbilityFxKind) => (
        HELD_FX.has(kind) && msg.durationMs ? msg.durationMs : ABILITY_FX_DURATION_MS[kind]
      );

      visuals.get(msg.playerId)?.playAbilityAnimation(ABILITY_FX_INTENT[visual.fx], ABILITY_ANIMATION_SECONDS);
      if (CASTER_FX.has(visual.fx)) {
        abilityFx.spawn({
          kind: visual.fx,
          colorHex: visual.colorHex,
          origin: anchorFor(msg.playerId),
          target: msg.targetId ? anchorFor(msg.targetId) : undefined,
          radiusM: msg.radiusM,
          durationMs: durationFor(visual.fx),
        });
      }
      if (caster) audioEngine.playAbilityCast({ x: caster.x, z: caster.z }, visual.fx);

      for (const hit of msg.hits ?? []) {
        const onHit = HIT_FX[visual.fx];
        if (onHit) {
          abilityFx.spawn({
            kind: onHit, colorHex: visual.colorHex, origin: anchorFor(hit.id), durationMs: durationFor(onHit),
          });
        }
        if (hit.damage === undefined) continue;
        const body = visuals.get(hit.id) ?? monsterVisuals.get(hit.id);
        const entity = room?.state.players.get(hit.id) ?? room?.state.monsters.get(hit.id);
        if (entity) {
          damageNumbers.spawn(String(hit.damage), hit.id === localSessionId ? 'taken' : 'dealt', entity.x, entity.z);
          audioEngine.playHitImpact({ x: entity.x, z: entity.z }, hit.damage, !room?.state.players.has(hit.id));
        }
        body?.playImpactVfx();
        if (!hit.died) void body?.playHitFlinch();
        if (hit.id === localSessionId) dScene.shakeCamera(SHAKE_HIT_TAKEN_M, SHAKE_DURATION_MS);
      }
      if (msg.restoredHp && msg.restoredHp > 0 && msg.targetId) {
        const healed = room?.state.players.get(msg.targetId);
        if (healed) damageNumbers.spawn(`+${msg.restoredHp}`, 'heal', healed.x, healed.z);
      }
    });

    room.onMessage('input.rejected', (msg: { reason: string; state?: string; action?: string }) => {
      // A refused jump lands early, silently — see the dungeon bootstrap.
      if (msg.action === 'jump') { hud.logCombatEvent(`rejected: jump ${msg.reason}`); localJump.cancel(); return; }
      hud.logCombatEvent(`rejected: ${msg.reason}`);
      abilityNotice.show(msg.reason, msg.state, abilityState?.resourceKind);
      audioEngine.playUiReject();
    });

    // design/gdd/pvp-arena.md Socket.io Events — {outcome: 'WIN'|'DRAW', winningTeamUserIds?}.
    // Resolved into a personal WIN/LOSE/DRAW by checking the local player's own userId.
    room.onMessage('arena.round_over', (msg: { outcome: 'WIN' | 'DRAW'; winningTeamUserIds?: string[] }) => {
      const personal: ArenaPersonalOutcome = msg.outcome === 'DRAW'
        ? 'DRAW'
        : (localUserId && msg.winningTeamUserIds?.includes(localUserId) ? 'WIN' : 'LOSE');
      hud.logCombatEvent(`ROUND OVER — ${personal}`);
      const survivors = [...(room?.state.players.values() ?? [])].filter((p) => p.state !== 'DEAD' && p.connected).length;
      const me = localSessionId ? room?.state.players.get(localSessionId) : undefined;
      roundOverlay.show(personal, {
        survivors,
        roundLengthS: roundStartedAtLocalMs === null ? undefined : (performance.now() - roundStartedAtLocalMs) / 1000,
        kills: localKills,
        damage: localDamageDealt,
        // A team can take the pit without you, and the sentence changes when
        // it does.
        survived: me ? me.state !== 'DEAD' : undefined,
      });
      combatHud.setVisible(false); // the results own the screen now
      roundBanner.setVisible(false);
      camera.releaseLook(); // the summary is a screen full of buttons; give the cursor back
      audioEngine.playRoundEnd(personal);
    });

    /**
     * Arena XP is outcome-based (leveling.ts explains why), so a level can only
     * cross at the round end — the server sends this to the one client it
     * happened to, right after `arena.round_over`. No pillar: the round is over
     * and the bodies are about to be disconnected, so the banner and the
     * fanfare carry it alone.
     */
    room.onMessage('character.levelup', (msg: { level: number }) => {
      levelUpBanner.show(msg.level);
      audioEngine.playLevelUp();
      hud.logCombatEvent(`LEVEL UP — level ${msg.level}`);
    });

    room.onLeave(() => {
      hud.logCombatEvent('disconnected from arena');
      if (!disposed) onMatchEnd?.();
    });

    input.onJump = () => jumpLocal();
    input.onAttack = (dir) => { if (room) { flushAim(); predictLocalState('ATTACK_WINDUP', dir); sendAttack(room, dir); } };
    touch?.start({
      onCycleTarget: () => cycleTarget(),
      onLook: (dx, dy) => camera.applyLookDelta(dx, dy),
      onAttack: () => { if (room) { flushAim(); predictLocalState('ATTACK_WINDUP', 'OVERHEAD'); sendAttack(room, 'OVERHEAD'); } },
      onBlockChange: (active) => {
        if (!room) return;
        if (active) { flushAim(); predictLocalState('BLOCKING'); }
        sendBlock(room, active);
      },
      onJump: () => jumpLocal(),
      // The arena has no doors, chests or cranks — there is nothing here to use.
      onInteractStart: () => {},
      onInteractEnd: () => {},
    });
    input.onParry = () => { if (room) { flushAim(); predictLocalState('PARRY_WINDOW'); sendParry(room); } };
    input.onSprintChange = (active) => { if (room) sendSprint(room, active); };
    input.onCrouchChange = (active) => { if (room) sendCrouch(room, active); };
    // Arena rework (2026-08-26) — the action bar and tab-target, both of which
    // the arena had no handler for at all.
    input.onAbilitySlot = (slot) => {
      if (!room) return;
      acquireTargetForAbility(slot);
      sendAbilitySlot(room, slot);
    };
    // Nearest living hostile, cycling past the one already selected. Players
    // first: in a pit the person is always the more urgent answer than the
    // skeleton, whatever the distances say (the monster bias). The rule itself
    // is `pickCycleTarget`, shared with the dungeon.
    input.onCycleTarget = () => cycleTarget();
    input.onSelectPartyMember = (index) => {
      if (!room || !localSessionId) return;
      const id = partyKeyTarget(index, localSessionId, partyFrameOrder);
      if (id) setLocalTarget(id, true);
    };
    combatHud.onSelectMember = (sessionId) => setLocalTarget(sessionId, true);
    actionBar.onActivate = (slot) => {
      if (!room) return;
      acquireTargetForAbility(slot);
      sendAbilitySlot(room, slot);
    };
  })().catch((err) => {
    console.error('[arena] failed to join room', err);
    hud.logCombatEvent(`connection failed: ${(err as Error).message ?? err}`);
  });

  /**
   * Tints a body by whose side it is on: blue for your team, red for everyone
   * else's. Applied once per body, and skipped for your own — you are never
   * confused about which one you are.
   */
  /** Whether a session id is on the local player's team. False for monsters, and for the local player itself. */
  /**
   * Give an ability a subject — see the same helper in
   * dungeon-instance-bootstrap.ts for the report that prompted it. The arena's
   * difference is that *players* are the enemies, so this picks the nearest
   * living non-teammate rather than the nearest monster.
   */
  function acquireTargetForAbility(slot: number): void {
    if (!room || !localSessionId) return;
    const me = room.state.players.get(localSessionId);
    if (!me) return;
    // 2026-10-16 — ENEMY abilities only, monsters as well as rivals (a
    // monster target used to be replaced by the nearest rival, because only
    // the players map was checked), in range and in front first.
    const ability = abilityState?.abilities.find((a) => a.slot === slot);
    if (!ability) return;
    const next = pickAbilityTarget({
      targeting: ability.targeting ?? 'ENEMY',
      rangeM: ability.rangeM,
      candidates: hostileCandidates(),
      me,
      currentId: localTargetId,
      currentIsExplicit: localTargetIsExplicit,
    });
    if (next) setLocalTarget(next, false);
  }

  /** Rival fighters and live monsters. The pit is lit, so everything streamed is seen. */
  function hostileCandidates(): TargetCandidate[] {
    if (!room || !localSessionId) return [];
    const out: TargetCandidate[] = [];
    for (const [id, other] of room.state.players.entries()) {
      if (id === localSessionId || !other.connected || isTeammate(id)) continue;
      out.push({ id, x: other.x, z: other.z, alive: other.state !== 'DEAD', revealed: true });
    }
    for (const [id, monster] of room.state.monsters.entries()) {
      out.push({ id, x: monster.x, z: monster.z, alive: monster.state !== 'DEAD', revealed: true, biasM: TARGET_MONSTER_BIAS_M });
    }
    return out;
  }

  /** Distance to what an ENEMY press would act on now, for the bar's range tint. */
  function hostileDistanceForBar(me: { x: number; z: number }): number | null {
    const candidates = hostileCandidates();
    const current = candidates.find((c) => c.id === localTargetId && c.alive);
    const pickId = current ? current.id : pickCycleTarget(candidates, me, '');
    const pick = pickId ? candidates.find((c) => c.id === pickId) : undefined;
    return pick ? Math.hypot(pick.x - me.x, pick.z - me.z) : null;
  }

  function cycleTarget(): void {
    if (!room || !localSessionId) return;
    const me = room.state.players.get(localSessionId);
    if (!me) return;
    // An implicit target is not a place in the cycle (QA defect C).
    const next = pickCycleTarget(hostileCandidates(), me, localTargetIsExplicit ? localTargetId : '');
    if (!next) { abilityNotice.showText(t('Nothing you can see to target')); return; }
    setLocalTarget(next, true);
  }

  /** The one place the soft target changes, and the server is always told — see the dungeon's twin. */
  function setLocalTarget(id: string, explicit: boolean): void {
    localTargetIsExplicit = explicit && id !== '';
    if (id === localTargetId) return;
    localTargetId = id;
    if (room) sendTarget(room, id);
  }

  function isTeammate(sessionId: string): boolean {
    if (!localSessionId || sessionId === localSessionId) return false;
    const local = room?.state.players.get(localSessionId);
    const other = room?.state.players.get(sessionId);
    return !!local && !!other && !!local.squadId && local.squadId === other.squadId;
  }

  function applyTeamColour(sessionId: string, visual: CharacterVisual): void {
    if (sessionId === localSessionId || rimApplied.has(sessionId)) return;
    const local = localSessionId ? room?.state.players.get(localSessionId) : undefined;
    const other = room?.state.players.get(sessionId);
    if (!local || !other || !other.squadId || !local.squadId) return;
    const ally = other.squadId === local.squadId;
    visual.applyRimLight(ally ? ALLY_RIM_COLOR : ENEMY_RIM_COLOR, RIM_POWER, RIM_INTENSITY);
    rimApplied.add(sessionId);
  }

  let lastFrameTime = performance.now();
  let rafHandle = 0;
  const frameTimer = new FrameTimer();

  function frame(): void {
    const now = performance.now();
    const rawDeltaMs = now - lastFrameTime;
    const dtSeconds = Math.min(0.1, rawDeltaMs / 1000);
    lastFrameTime = now;
    frameTimer.push(rawDeltaMs); // see dungeon-instance-bootstrap.ts on why this uses the raw, unclamped delta

    const localPlayerLive = room && localSessionId ? room.state.players.get(localSessionId) : undefined;
    // camera-and-controls.md amendment (2026-08-25, fifth) — mouse-look; see
    // dungeon-instance-bootstrap.ts's identical block. PvP and PvE share this
    // rig deliberately: a control scheme learned in one has to hold in the other.
    camera.update(dtSeconds);
    if (!cameraAligned && localPlayerLive) {
      camera.alignTo(localPlayerLive.facing);
      cameraAligned = true;
    }

    // The stick, handed to the one thing that owns a move vector. Sprint is an
    // edge because the server hears about it in a message, not in the packet.
    if (touch) {
      input.touchDirection = touch.direction;
      if (touch.sprintHeld !== touchSprintSent) {
        touchSprintSent = touch.sprintHeld;
        input.onSprintChange?.(touchSprintSent);
      }
    }
    const dir = camera.worldDirection(input.direction);
    const directionChanged = Math.abs(dir.x - lastSentDirection.x) > DIRECTION_SEND_EPSILON
      || Math.abs(dir.z - lastSentDirection.z) > DIRECTION_SEND_EPSILON;
    if (room && directionChanged && now - lastMoveSentAt >= MOVE_SEND_INTERVAL_MS) {
      lastSentDirection = dir;
      lastMoveSentAt = now;
      sendMove(room, dir);
    }

    const aimChanged = lastSentAimYaw === null
      || Math.abs(normalizeAngle(camera.viewYaw - lastSentAimYaw)) > AIM_SEND_EPSILON;
    if (room && aimChanged && now - lastAimSentAt >= AIM_SEND_INTERVAL_MS) {
      lastSentAimYaw = camera.viewYaw;
      lastAimSentAt = now;
      sendAim(room, camera.viewYaw);
    }

    if (room && localSessionId) {
      const localPlayer = room.state.players.get(localSessionId);
      if (localPlayer) {
        hud.updateStats({
          pingMs: clockSync?.rtt ?? 0, state: localPlayer.state as EntityStateName, hp: localPlayer.hp, stamina: localPlayer.stamina,
          fps: frameTimer.fps, frameMs: frameTimer.meanMs, worstFrameMs: frameTimer.worstMs,
        });

        // ── Arena rework (2026-08-26): the HUD, fed the same way the dungeon feeds it ──
        const serverNow = estimatedServerTimeNow();
        combatHud.setVitals({
          hp: localPlayer.hp,
          hpMax: localPlayer.hpMax,
          stamina: localPlayer.stamina,
          staminaMax: localPlayer.staminaMax,
          resource: abilityState?.resource ?? 0,
          resourceMax: abilityState?.resourceMax ?? 0,
          resourceKind: abilityState?.resourceKind ?? '',
          characterClass: localPlayer.characterClass as CharacterClassId,
          nickname: localPlayer.nickname,
        });

        // Your team, if you have one. In a solo free-for-all this is correctly
        // empty — every other body in the pit is an enemy.
        const team: HudPartyMember[] = [];
        for (const [sessionId, other] of room.state.players.entries()) {
          if (sessionId === localSessionId || !isTeammate(sessionId)) continue;
          team.push({
            sessionId,
            nickname: other.nickname || sessionId.slice(0, 4),
            characterClass: other.characterClass as CharacterClassId,
            role: rosterEntry(other.characterClass as CharacterClassId).role,
            hp: other.hp,
            hpMax: other.hpMax,
            isDead: other.state === 'DEAD',
            // The arena has no downed window — a death there is a round loss,
            // not a body to run to. `pvp-arena.md`'s revive is still unbuilt.
            reviveSecondsLeft: null,
            isLocal: false,
            distanceM: Math.hypot(other.x - localPlayer.x, other.z - localPlayer.z),
          });
        }
        partyFrameOrder = team.map((m) => m.sessionId);
        for (const member of team) member.isTargeted = member.sessionId === localTargetId;
        combatHud.setParty(team);

        // The target frame — a rival fighter by name, or a neutral mob by
        // archetype. The dungeon's own version only ever handled monsters;
        // in a pit the answer is usually a person.
        const targetPlayer = localTargetId ? room.state.players.get(localTargetId) : undefined;
        const targetMonster = localTargetId ? room.state.monsters.get(localTargetId) : undefined;
        // 2026-10-16: "out of range" against the longest reach of the
        // abilities that could act on it, a floor ring under it, and the
        // server told when the lock is released.
        const reachOf = (kind: 'ENEMY' | 'ALLY'): number => {
          const ranges = (abilityState?.abilities ?? [])
            .filter((a) => a.targeting === kind && a.rangeM !== undefined)
            .map((a) => a.rangeM as number);
          return ranges.length > 0 ? Math.max(...ranges) : Infinity;
        };
        if (targetPlayer && targetPlayer.state !== 'DEAD') {
          const hostile = !isTeammate(localTargetId) && localTargetId !== localSessionId;
          const distanceM = Math.hypot(targetPlayer.x - localPlayer.x, targetPlayer.z - localPlayer.z);
          combatHud.setTarget({
            name: targetPlayer.nickname || localTargetId.slice(0, 4),
            hp: targetPlayer.hp,
            hpMax: targetPlayer.hpMax,
            distanceM,
            isHostile: hostile,
            outOfRange: distanceM > reachOf(hostile ? 'ENEMY' : 'ALLY'),
          });
          targetRing.update(visuals.get(localTargetId)?.root.position ?? null, hostile, dtSeconds);
        } else if (targetMonster && targetMonster.state !== 'DEAD') {
          const distanceM = Math.hypot(targetMonster.x - localPlayer.x, targetMonster.z - localPlayer.z);
          combatHud.setTarget({
            name: monsterName(targetMonster.archetype),
            hp: targetMonster.hp,
            hpMax: targetMonster.hpMax,
            distanceM,
            isHostile: true,
            outOfRange: distanceM > reachOf('ENEMY'),
          });
          targetRing.update(monsterVisuals.get(localTargetId)?.root.position ?? null, true, dtSeconds);
        } else {
          combatHud.setTarget(null);
          targetRing.update(null, true, dtSeconds);
          // A dead target releases the implicit lock, so the frame moves on to
          // whatever you hit next rather than showing a corpse.
          if (localTargetId) setLocalTarget('', false);
        }

        if (abilityState) {
          actionBar.setTargetContext({ distanceM: hostileDistanceForBar(localPlayer) });
          actionBar.update(abilityState, serverNow);
          actionBar.refreshCooldowns(serverNow);
          const started = abilityState.castStartedAt ?? 0;
          const ends = abilityState.castEndsAt ?? 0;
          const casting = ends > serverNow && ends > started;
          actionBar.setCast(casting ? (serverNow - started) / (ends - started) : null);
        }

        // The round itself: the bell, the clock, and who is left.
        let fightersAlive = 0;
        for (const other of room.state.players.values()) {
          if (other.state !== 'DEAD' && other.connected) fightersAlive += 1;
        }
        roundBanner.update({
          phase: room.state.roundState as ArenaPhase,
          countdownS: Math.ceil((room.state.roundStartsAt - serverNow) / 1000),
          remainingS: Math.ceil((room.state.roundEndsAt - serverNow) / 1000),
          teamsAlive: room.state.teamsAlive,
          fightersAlive,
        }, now);
        // Game Feel Polish Phase 3 — see dungeon-instance-bootstrap.ts's identical call.
        const localState = localPlayer.state as EntityStateName;
        const localAirborne = localJump.airborneAt(estimatedServerTimeNow());
        audioEngine.updateLocalFootsteps(
          dtSeconds, !localAirborne && (localState === 'MOVING' || localState === 'SPRINTING'), localState === 'SPRINTING',
          ARENA_FOOTSTEP_STRATUM,
        );

        /*
         * Opponents' footsteps. The arena has no strata, so it borrows the
         * Cellars bank — its maps are the same stone the Cellars are built from,
         * and a bespoke arena footstep bank would be four more files to hear the
         * same surface.
         */
        const audibleBodies: FootstepEntity[] = [];
        for (const [id, other] of room.state.players.entries()) {
          if (id === localSessionId) continue;
          audibleBodies.push({
            id, x: other.x, z: other.z, state: other.state,
            airborne: other.jumpStartedAt > 0 && isAirborne(estimatedServerTimeNow() - other.jumpStartedAt),
          });
        }
        for (const [id, monster] of room.state.monsters.entries()) {
          if (monster.state === 'DEAD') continue;
          audibleBodies.push({ id, x: monster.x, z: monster.z, state: monster.state });
        }
        for (const step of remoteFootsteps.update(dtSeconds, audibleBodies)) {
          audioEngine.playRemoteFootstep({ x: step.x, z: step.z }, ARENA_FOOTSTEP_STRATUM, step.isSprinting, step.gainMultiplier);
        }
      }
    }

    const renderNow = estimatedServerTimeNow();
    for (const [sessionId, interp] of remoteInterpolators.entries()) {
      const sample = interp.sample(renderNow);
      const visual = visuals.get(sessionId);
      if (!sample || !visual) continue;
      const isLocal = sessionId === localSessionId;
      // Jump (2026-10-16): lifts the body only; the camera follows the grounded sample.
      const jumpY = isLocal
        ? localJump.offsetAt(renderNow)
        : remoteJumpOffset(renderNow, room?.state.players.get(sessionId)?.jumpStartedAt ?? 0);
      visual.setPose(sample.x, jumpY, sample.z, isLocal ? localRenderFacing(sample.facing) : sample.facing);
      if (isLocal) {
        // Game Feel Polish Phase 4 — FOV sprint kick (the dash half went with the dodge roll).
        const localMoveState = room?.state.players.get(localSessionId)?.state as EntityStateName | undefined;
        const sprintOrDashActive = localMoveState === 'SPRINTING';
        dScene.followLocalPlayer(sample, camera.viewYaw, camera.viewPitch, camera.shoulderSide, dtSeconds, sprintOrDashActive);
        // The arena has no occlusion-fade pass of its own, but it shares the
        // camera rig — and MIN_CAMERA_DISTANCE was lowered so wall hits are no
        // longer overridden, which means the eye can now close right up on your
        // own body. Without this the arena would trade "inside a wall" for
        // "inside your own head." See render/proximity-fade.ts.
        visual.setCameraFade(localPlayerFadeAlpha(dScene.cameraBoomLengthM));
      }
    }
    audioEngine.setListenerPose({ x: dScene.camera.position.x, z: dScene.camera.position.z }, camera.viewYaw);

    const animDtSeconds = now < hitStopUntilMs ? dtSeconds * HIT_STOP_TIME_SCALE : dtSeconds;
    for (const visual of visuals.values()) visual.update(animDtSeconds);

    for (const [monsterId, interp] of monsterInterpolators.entries()) {
      const sample = interp.sample(renderNow);
      const visual = monsterVisuals.get(monsterId);
      if (!sample || !visual) continue;
      visual.setPose(sample.x, 0, sample.z, sample.facing);
    }
    for (const visual of monsterVisuals.values()) visual.update(animDtSeconds);

    abilityFx.update(dtSeconds);

    // Projected after the camera pose is final, so a number stays pinned to
    // the body it belongs to for its whole life.
    damageNumbers.update(dtSeconds * 1000, {
      project: (x, y, z) => {
        projectVec.set(x, y, z).project(dScene.camera);
        if (projectVec.z > 1) return null; // behind the camera
        return {
          xPx: (projectVec.x * 0.5 + 0.5) * window.innerWidth,
          yPx: (-projectVec.y * 0.5 + 0.5) * window.innerHeight,
        };
      },
    });

    dScene.render();

    // Lifted here rather than from a callback, because "the world is ready" is
    // a claim about the *picture*, and this is the only place that knows one
    // was drawn. See ui/entry-veil.ts.
    if (entryVeil.isVisible) {
      if (layoutReady && localSessionId && visuals.has(localSessionId)) framesWithWorld += 1;
      if (framesWithWorld >= 2 || now >= veilDeadlineMs) {
        entryVeil.dismiss();
        // The server holds the bell until every client says this — see
        // ArenaRoom.checkRoundStart. Sent on the timeout path too: a client
        // that gave up waiting is still a client that is looking at the room,
        // and the alternative is making everyone else wait out the 20s join
        // grace for it.
        if (room) room.send('world.ready', {});
      }
    }

    rafHandle = requestAnimationFrame(frame);
  }
  rafHandle = requestAnimationFrame(frame);

  currentTeardown = () => {
    disposed = true;
    cancelAnimationFrame(rafHandle);
    input.stop();
    camera.stop();
    touch?.dispose();
    canvas.removeEventListener('mousedown', onCanvasMouseDown);
    canvas.removeEventListener('mouseup', onCanvasMouseUp);
    runMenu.dispose();
    clockSync?.stop();
    unbindSettings.forEach((off) => off());
    audioEngine.dispose();
    room?.leave();
    for (const visual of visuals.values()) visual.dispose();
    visuals.clear();
    for (const visual of monsterVisuals.values()) visual.dispose();
    monsterVisuals.clear();
    abilityFx.dispose();
    dScene.dispose();
    hud.dispose();
    combatHud.dispose();
    actionBar.dispose();
    abilityNotice.dispose();
    targetRing.dispose();
    damageNumbers.dispose();
    roundBanner.dispose();
    damageFlash.dispose();
    roundOverlay.dispose();
    mouseLookPrompt.dispose();
    levelUpBanner.dispose();
    entryVeil.dispose();
    canvas.remove();
  };
  return currentTeardown;
}

export function unmountArena(): void {
  currentTeardown?.();
  currentTeardown = null;
}
